import { ReactNode, useState } from "react";
import Button from "components/ui/Button";
import { GhostButton, DangerButton } from "components/adminUi/Button";
import Dialog from "./index";
import styles from "./ConfirmDialog.module.scss";

type BaseConfirmDialogProps = {
  title: string;
  /** Short confirmation text; use children for richer bodies (either works alone). */
  message?: ReactNode;
  confirmLabel: string;
  /** Cancel label — pass a translated string on learner-facing surfaces
   * (the default is English per the internal-tools policy). */
  cancelLabel?: string;
  /** Renders the confirm action in the destructive style. */
  danger?: boolean;
  /** Called with true after a successful confirm, with no argument on cancel. */
  onClose: (confirmed?: boolean) => void;
  children?: ReactNode;
};

type ConfirmDialogProps =
  | (BaseConfirmDialogProps & {
      acknowledgeOnly?: false;
      /** The action to run on confirm; a rejection keeps the dialog open with the error. */
      onConfirm: () => Promise<unknown>;
    })
  | (BaseConfirmDialogProps & {
      /** Single-acknowledgement mode: no Cancel, the confirm label is the OK label. */
      acknowledgeOnly: true;
      /** Optional in acknowledge mode, and may be synchronous. */
      onConfirm?: () => Promise<unknown> | unknown;
    });

/** Both legacy confirmation dialogs' error shapes: promise services reject with
 * an Error; callback-style admin services reject with the raw error response
 * ({ errors: [{ description }] }). */
function describeError(err: unknown): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }
  const description = (err as { errors?: { description?: string }[] } | undefined)
    ?.errors?.[0]?.description;
  return description ?? "The action failed. Try again.";
}

/**
 * Promise-first confirmation on the ui/Dialog shell — the merged successor of
 * adminUi's ConfirmDialog and DeleteDialog (One-Kit merge rung #2). Wrap
 * callback-style services in a Promise at the call site.
 */
export default function ConfirmDialog(props: ConfirmDialogProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  async function confirm() {
    setPending(true);
    setError("");
    try {
      await props.onConfirm?.();
      props.onClose(true);
    } catch (err) {
      setPending(false);
      setError(describeError(err));
    }
  }

  const ConfirmButton = props.danger ? DangerButton : Button;

  return (
    <Dialog
      open
      size="sm"
      title={props.title}
      onClose={() => props.onClose()}
      footer={
        <>
          {!props.acknowledgeOnly && (
            <GhostButton onClick={() => props.onClose()} disabled={pending}>
              {props.cancelLabel ?? "Cancel"}
            </GhostButton>
          )}
          <ConfirmButton onClick={confirm} disabled={pending}>
            {pending ? "Working…" : props.confirmLabel}
          </ConfirmButton>
        </>
      }
    >
      {props.message !== undefined && props.message !== null && (
        <p className={styles.message}>{props.message}</p>
      )}
      {props.children}
      {error && <p className={styles.error}>{error}</p>}
    </Dialog>
  );
}
